<template>
  <div
    :class="[
      'input-date-container',
      { 'input-date-container--webcomponent': isWebComponent },
    ]"
  >
    <InputContainer
      ref="inputContainerRef"
      v-model="modelValue"
      :size
      :validation-state
      :icon-state
      :icon-name
      :readonly
      :disabled
      :deletable="false"
      :input-type
      :alertLabel
      :alertType
      :read-only-reason
    >
    <input
      type="text"
      ref="inputHtml"
      :id
      :value="texte"
      :placeholder
      :readonly
      :disabled
      :maxlength
      @input="textChanged"
      @blur="onBlur"
      @focus="onFocus"
      :style="disabled === true && modelValue?.length && modelValue?.length > 0 ? 'color: black !important;' : ''"
    />
    <BasicIcon
      v-if="hasCalendar && !disabled && !readonly"
      ref="calendarIconRef"
      @click="toggleCalendar()"
      name="calendar"
      class="calendar--icon"
      pointer
    />
  </InputContainer>
    <Teleport to="body" :disabled="isWebComponent">
      <ClickOutside
        v-if="hasCalendar && !disabled && !readonly && showCalendarDesktop && (isDesktop || isTablet)"
        @close="closeFromOutside"
        ref="datepickerContainerRef"
        class="datepicker-container datepicker-container--teleported"
        :class="`datepicker-container--${calendarDirection}`"
        :style="isWebComponent ? undefined : calendarStyle"
      >
        <VueDatePicker
          v-model="calendarModel"
          :inline="true"
          :textInput="false"
          :clearable="false"
          hide-input-icon
          auto-apply
          :locale="fr"
          class="calendarDesktop"
          @closed="showCalendarDesktop = false"
        />
      </ClickOutside>
      <ModalComponent
        v-if="showCalendarMobile"
        class="calendarMobile"
        v-model="showCalendarMobile"
      >
        <template #header>Choisissez une date</template>
        <template #content>
          <VueDatePicker
            v-model="calendarModel"
            inline
            auto-apply
            :locale="fr"
          />
        </template>
      </ModalComponent>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
  import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
  import { VueDatePicker } from '@vuepic/vue-datepicker'
  import { fr } from 'date-fns/locale'
  import '@vuepic/vue-datepicker/dist/main.css'
  import ModalComponent from '@/features/interface/modal/ModalComponent.vue'
  import {
    useAutoId,
    type AlertInputProps,
    type InputDateEvents,
    type InputDateModel,
    type InputDateProps,
    type InputProps,
  } from '@designSystem/components'
  import { useMenuPositioning, useParentClickOutsidePause } from '@designSystem/composables'
  import InputContainer from '@designSystem/components/wrapper/inputContainer/InputContainer.vue'
  import { dateTimeToReadableDate, isDate, readableDatetoDateTime } from '@/features/shared/tools/date'
  import { isNullUndefinedOrEmptyString } from '@/features/shared/tools/object'
  import { DEVICE_BREAKPOINT } from '@/plugin/device-breakpoint'
  import { useWebComponentNode } from '@/features/interface/composables/useWebComponentNode'

  const webComponentNode = useWebComponentNode()
  const isWebComponent = computed(() => !!webComponentNode.shadowRoot)

  const props = withDefaults(defineProps<InputDateProps & InputProps & AlertInputProps>(), {
    size: 'medium',
    iconName: undefined,
    readonly: false,
    deletable: false,
    hasCalendar: true,
    placeholder: 'jj/mm/aaaa',
  })

  const { isDesktop, isTablet, isMobile } = inject(DEVICE_BREAKPOINT)!

  const id = useAutoId('input-date')
  const emit = defineEmits<InputDateEvents>()
  const inputHtml = ref<HTMLInputElement | null>(null)
  const calendarIconRef = ref<any>(null)
  const datepickerContainerRef = ref<HTMLElement | null>(null)
  const inputContainerRef = ref<InstanceType<typeof InputContainer> | null>(null)
  const inputContainerEl = computed(() => (inputContainerRef.value?.$el as HTMLElement) ?? null)
  const modelValue = defineModel<InputDateModel>()
  const estModifie = ref(false)
  const estModifiable = computed(() => !props.readonly && !props.disabled)

  const showCalendarMobile = ref(false)
  const showCalendarDesktop = ref(false)

  useParentClickOutsidePause(showCalendarDesktop)

  const { menuStyle: calendarStyle, menuDirection: calendarDirection } = useMenuPositioning({
    anchorRef: inputContainerEl,
    isOpen: showCalendarDesktop,
    minBottomSpace: 320, // Hauteur approximative du calendrier
  })

  const handleResize = () => {
    showCalendarMobile.value = false
    showCalendarDesktop.value = false
  }

  const closeFromOutside = (event: MouseEvent) => {
    if (!showCalendarDesktop.value) return

    const target = event.target as Node

    // Vérifier si le clic est sur l'icône calendrier
    if (calendarIconRef.value) {
      const iconElement = calendarIconRef.value.$el || calendarIconRef.value
      if (iconElement?.contains(target)) {
        return
      }
    }

    // Vérifier si le clic est sur l'input container
    const containerEl = inputContainerRef.value?.$el
    if (containerEl?.contains(target)) {
      return
    }

    // Fermer le calendrier
    showCalendarDesktop.value = false
  }

  onMounted(() => {
    window.addEventListener('resize', handleResize)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('resize', handleResize)
  })

  const formater = (value?: InputDateModel) => {
    if (!isDate(value)) {
      return value ?? ''
    }

    const tokens = value.split('-')
    return `${tokens[2]}/${tokens[1]}/${tokens[0]}`
  }

  const texte = ref<string>(formater(props.modelValue))

  const calendarModel = computed({
    get: () => readableDatetoDateTime(props.modelValue),
    set: (value) => {
      const readable = dateTimeToReadableDate(value as any)
      texte.value = readable
      emit('update:modelValue', tryParse(readable) || null)

      // Fermer les calendriers après sélection
      if (isMobile.value) {
        showCalendarMobile.value = false
      } else {
        showCalendarDesktop.value = false
      }
    },
  })

  if (estModifiable.value && props.defaultValue && isNullUndefinedOrEmptyString(modelValue)) {
    nextTick(() => {
      if (modelValue.value !== props.defaultValue) {
        emit('update:modelValue', props.defaultValue!)
      }
    })
  }

  const retireCaracteresNonAutorises = (value: string): string => value.replace(/[^\d/^-]+/g, '')

  const tryParse = (value: string): string | null => {
    let text = value.replace(/\//g, '-')
    if (text !== '') {
      const tokens = text.split('-')
      if (tokens[0]?.length === 1) tokens[0] = `0${tokens[0]}`
      if (tokens[1]?.length === 1) tokens[1] = `0${tokens[1]}`
      text = `${tokens[2]}-${tokens[1]}-${tokens[0]}`
      if (isDate(text)) return text
    }
    return null
  }

  const textChanged = (event: Event) => {
    estModifie.value = true

    const inputChaine = (event.target as HTMLInputElement).value
    const cleanChaine = retireCaracteresNonAutorises(inputChaine)

    texte.value = (event.target as HTMLInputElement).value = cleanChaine

    const modelChaine = tryParse(cleanChaine)
    emit('update:modelValue', modelChaine || null)
  }

  const onFocus = () => {
    estModifie.value = false
  }

  const onBlur = (event: FocusEvent): void => {
    if (!estModifiable.value) return

    const newValue = ((event.target as HTMLInputElement).value ?? '').trim()
    let strDate: string | undefined | null
    if (newValue === '' && isDate(props.defaultValue)) {
      strDate = props.defaultValue
      emit('update:modelValue', strDate || null)
    } else {
      if (!estModifie.value) return

      strDate = tryParse(newValue)
    }

    texte.value = (event.target as HTMLInputElement).value = formater(strDate)
  }

  watch(
    () => props.modelValue,
    (newValue) => {
      if (document.activeElement !== inputHtml.value) {
        texte.value = formater(newValue)
      }
    },
  )

  const toggleCalendar = () => {
    if (isDesktop.value || isTablet.value) {
      showCalendarDesktop.value = !showCalendarDesktop.value
      showCalendarMobile.value = false
    } else {
      showCalendarMobile.value = !showCalendarMobile.value
      showCalendarDesktop.value = false
    }
  }
</script>

<style lang="less">
  @import '../input/BasicInput.less';
  @import './BasicInputDate.less';
</style>
